import {
  Avatar,
  Box,
  Center,
  Flex,
  Image,
  Spacer,
  Text,
} from "@chakra-ui/react";
import { Modal, Tooltip } from "antd";
import useReservaDetalhes from "core/features/reservas/hooks/useReservaDetalhes";
import { StatusReservaEnum } from "core/features/reservas/typings";
import colors from "core/resources/theme/colors";
import moment from "moment";
import { AiFillMessage } from "react-icons/ai";
import { HiPhone } from "react-icons/hi";
import OutlineButton from "ui/components/Button/OutlineButton";
import PrimaryButton from "ui/components/Button/PrimaryButton";
import { formatHorario } from "../../pages/Admin/Administracao/Reservas/Listagem/util/helper";
import { useLoading } from "ui/components/GlobalLoading";
import { PermissaoEnum } from "core/resources/enums/permissao";
import { useRegrasTenant } from "core/features/tenants/tenants.context";

interface ModalDetalheReservaProps {
  isOpen: boolean;
  onClose: () => any;
  reservaId: string;
  onSendMessage?: () => any;
  onConfirm?: (id: string) => any;
  onCancel?: (id: string) => any;
  isLoadingCancelar?: boolean;
  isLoadingConfirmar?: boolean;
}

function ModalDetalheReserva({
  isOpen,
  onCancel,
  onConfirm,
  onClose,
  reservaId,
  onSendMessage,
  isLoadingCancelar,
  isLoadingConfirmar,
}: ModalDetalheReservaProps) {
  const { useSubtenantsViews } = useRegrasTenant();

  const {
    data: reserva,
    isLoading,
    isFetching,
  } = useReservaDetalhes(reservaId);

  useLoading(isLoading || isFetching);

  return (
    <Modal
      visible={isOpen}
      onCancel={onClose}
      width="600px"
      centered
      title="Detalhamento da Reserva"
      footer={
        <>
          <Flex gridGap="24px" mt="auto" justifyContent="flex-end">
            <OutlineButton onClick={onClose}>Fechar</OutlineButton>
            {reserva?.linkBoleto ? (
              <OutlineButton
                onClick={() => window.open(reserva?.linkBoleto, "blank")}
                isLoading={isLoadingConfirmar || isLoading || isFetching}
              >
                Ver Boleto
              </OutlineButton>
            ) : null}
            {onCancel &&
            reserva &&
            reserva?.statusId !== StatusReservaEnum.Cancelada ? (
              <PrimaryButton
                permissions={[
                  PermissaoEnum.Backoffice.Administrativo.Reserva.Cancelar,
                ]}
                bgColor="red.500"
                isLoading={isLoadingCancelar || isFetching}
                onClick={() => onCancel?.(reserva?.id!)}
              >
                Cancelar Reserva
              </PrimaryButton>
            ) : null}
            {[
              StatusReservaEnum.AguardandoPagamento,
              StatusReservaEnum.Pendente,
            ].includes(reserva?.statusId!) ? (
              <PrimaryButton
                permissions={[
                  PermissaoEnum.Backoffice.Administrativo.Reserva.Cancelar,
                ]}
                onClick={() => onConfirm?.(reserva?.id!)}
                isLoading={isLoadingConfirmar || isLoading || isFetching}
              >
                Confirmar
              </PrimaryButton>
            ) : null}
          </Flex>
        </>
      }
    >
      <Flex>
        <Box>
          <Avatar
            name={reserva?.pessoa.nome}
            src={reserva?.pessoa.imagemUrl}
            mr={4}
          />
        </Box>
        <Flex flexDir="column">
          <Text fontWeight="bold">{reserva?.pessoa.nome}</Text>
          <Text>
            Unidade {reserva?.unidade.numero}{" "}
            {reserva?.unidade.blocoQuadra
              ? "Bloco/Quadra " + reserva.unidade.blocoQuadra
              : null}
          </Text>
        </Flex>
        <Spacer />
        {reserva?.pessoa.celular ? (
          <Center mr={onSendMessage ? 4 : 0}>
            <Tooltip title={reserva?.pessoa.celular}>
              <HiPhone fontSize={24} color={colors.blue} />
            </Tooltip>
          </Center>
        ) : null}
        {onSendMessage ? (
          <Center cursor="pointer" onClick={onSendMessage}>
            <AiFillMessage fontSize={24} color={colors.blue} />
          </Center>
        ) : null}
      </Flex>
      <Flex mt="26px">
        {reserva?.areaComum.imagemUrl ? (
          <Image
            h="110px"
            w="150px"
            mr={6}
            objectFit="cover"
            src={reserva?.areaComum.imagemUrl}
            borderRadius="14px"
          />
        ) : null}
        <Flex flexDir="column" gap={4}>
          <Flex flexDir="column">
            <Text color="#8792a5">ÁREA COMUM:</Text>
            <Text>{reserva?.areaComum.nome}</Text>
          </Flex>

          <Flex flexDir="column">
            <Text color="#8792a5">DATA:</Text>
            <Text>
              {reserva?.data
                ? moment(reserva?.data).format("DD/MM/YYYY")
                : "--"}
            </Text>
          </Flex>

          <Flex flexDir="column">
            <Text color="#8792a5">DATA DE CRIAÇÃO:</Text>
            <Text>
              {reserva?.data
                ? moment(reserva?.createdAt).format("DD/MM/YYYY [às] HH:mm:ss")
                : "--"}
            </Text>
          </Flex>
          {reserva?.dataCancelamento ? (
            <Flex flexDir="column">
              <Text color="#8792a5">DATA DE CANCELAMENTO:</Text>
              <Text>
                {moment(reserva?.dataCancelamento).format("DD/MM/YYYY") ?? "--"}
              </Text>
            </Flex>
          ) : null}
          {reserva?.origem ? (
            <Flex flexDir="column">
              <Text color="#8792a5">ORIGEM:</Text>
              <Text>{reserva.origem.toLocaleUpperCase()}</Text>
            </Flex>
          ) : null}
          {useSubtenantsViews && reserva?.tenant ? (
            <Flex flexDir="column">
              <Text color="#8792a5">CONDOMÍNIO:</Text>
              <Text>{reserva.tenant.nome}</Text>
            </Flex>
          ) : null}
        </Flex>
        <Spacer />
        <Flex flexDir="column" gap={4}>
          <Flex flexDir="column">
            <Text color="#8792a5">NOME DO EVENTO:</Text>
            <Text>{reserva?.nome ?? "---"}</Text>
          </Flex>

          <Flex flexDir="column">
            <Text color="#8792a5">HORÁRIO:</Text>
            <Text>{formatHorario(reserva?.horaInicio, reserva?.horaFim)} </Text>
          </Flex>

          <Flex flexDir="column">
            <Text color="#8792a5">NOME DO RESERVANTE:</Text>
            <Text>{reserva?.pessoaCriadora?.nome ?? "--"}</Text>
          </Flex>
          {reserva?.pessoaCancelamento ? (
            <Flex flexDir="column">
              <Text color="#8792a5">QUEM CANCELOU:</Text>
              <Text>{reserva?.pessoaCancelamento?.nome ?? "--"}</Text>
            </Flex>
          ) : null}
        </Flex>
      </Flex>
    </Modal>
  );
}

export default ModalDetalheReserva;
